import { FC, useEffect } from "react";
import { IconButton, Stack, Typography } from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { useShallow } from "zustand/shallow";
import { useUnmount } from "react-use";

import { useListBuilderStore } from "stores/list-builder.store";
import { List } from "pages/Kpis/KpisDialogs/List";
import { KpisMachinesWithDates } from "interfaces/kpis";
import { Fieldset } from "components/Fieldset/Fieldset";

interface Props {
  availableItems: KpisMachinesWithDates[];
  selectedItems: KpisMachinesWithDates[];
  disabled?: boolean;
}

export const ListBuilder: FC<Props> = ({
  availableItems,
  selectedItems,
  disabled = false
}): JSX.Element => {
  const {
    available,
    selected,
    filterValue,
    availableChecked,
    selectedChecked,
    filteredAvailable,
    filteredSelected,
    init,
    addAvailable,
    addSelected,
    updateFilteredLists,
    reset
  } = useListBuilderStore(
    useShallow(store => ({
      available: store.available,
      selected: store.selected,
      filterValue: store.filterValue,
      availableChecked: store.availableChecked,
      selectedChecked: store.selectedChecked,
      filteredAvailable: store.filteredAvailable,
      filteredSelected: store.filteredSelected,
      init: store.init,
      addAvailable: store.addAvailable,
      addSelected: store.addSelected,
      updateFilteredLists: store.updateFilteredLists,
      reset: store.reset
    }))
  );

  useEffect(() => {
    init(availableItems, selectedItems);
  }, [availableItems, init, selectedItems]);

  useEffect(() => {
    // Only pass an object of key-value pairs for filtering
    // If filterValue is not an object, use an empty object
    const filterObj: Partial<KpisMachinesWithDates> =
      filterValue && typeof filterValue === "object" ? filterValue : {};
    updateFilteredLists(filterObj);
  }, [filterValue, available, selected, updateFilteredLists]);

  useUnmount(() => reset());

  const handleAdd = (): void => {
    addSelected(availableChecked);
  };

  const handleRemove = (): void => {
    addAvailable(selectedChecked);
  };

  return (
    <>
      <Stack component="section" direction="row" spacing={1}>
        <Stack flex={1}>
          <Fieldset>
            <legend>
              Available ({filteredAvailable.length} of {available.length})
            </legend>

            {filteredAvailable.length > 0 ? (
              <List type="available" disabled={disabled} />
            ) : (
              <Typography>Nothing available</Typography>
            )}
          </Fieldset>
        </Stack>

        <Stack justifyContent="center" alignItems="center">
          <IconButton
            size="large"
            aria-label="add"
            onClick={handleAdd}
            disabled={disabled || !availableChecked.length}
          >
            <ChevronRight />
          </IconButton>
          <IconButton
            size="large"
            aria-label="remove"
            onClick={handleRemove}
            disabled={disabled || !selectedChecked.length}
          >
            <ChevronLeft />
          </IconButton>
        </Stack>

        <Stack flex={1}>
          <Fieldset sx={{ minHeight: "100%" }}>
            <legend>
              Selected ({filteredSelected.length} of {selected.length})
            </legend>

            {filteredSelected.length > 0 ? (
              <List type="selected" disabled={disabled} />
            ) : (
              <Typography>Nothing selected</Typography>
            )}
          </Fieldset>
        </Stack>
      </Stack>
    </>
  );
};
